import { useEffect, useState } from "react";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { Bitcoin, Coins, Landmark, Copy, Check } from "lucide-react";
import { api, fmtUSD, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

function CopyRow({ label, value, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-subtle last:border-0">
      <div className="min-w-0 flex-1">
        <div className="label-eyebrow mb-1">{label}</div>
        <div className="font-mono-num text-sm break-all" data-testid={testId}>{value || <span className="text-muted-foreground">— not configured —</span>}</div>
      </div>
      {value && (
        <button onClick={copy} className="p-1.5 hover:bg-surface-hover rounded shrink-0" title="Copy">
          {copied ? <Check className="w-4 h-4 text-profit" /> : <Copy className="w-4 h-4 text-muted-foreground" />}
        </button>
      )}
    </div>
  );
}

export default function ClientDeposit() {
  const [methods, setMethods] = useState(null);
  const [tab, setTab] = useState("btc");
  const [form, setForm] = useState({ amount: "", reference: "", note: "" });
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadHistory = () =>
    api.get("/client/transactions", { params: { type: "deposit" } }).then((r) => setHistory(r.data));

  useEffect(() => {
    api.get("/client/deposit-methods").then((r) => setMethods(r.data));
    loadHistory();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) return toast.error("Enter a valid amount");
    const methodLabel = tab === "btc" ? "Bitcoin (BTC)" : tab === "usdt" ? `USDT (${methods?.usdt?.network || "TRC20"})` : "Bank Transfer";
    setBusy(true);
    try {
      await api.post("/client/deposits", { ...form, amount, method: methodLabel });
      toast.success("Deposit request submitted — awaiting admin approval");
      setForm({ amount: "", reference: "", note: "" });
      loadHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const btcAddr = methods?.btc?.address || "";
  const usdtAddr = methods?.usdt?.address || "";
  const usdtNet = methods?.usdt?.network || "TRC20";
  const bank = methods?.bank || {};

  return (
    <div className="space-y-6" data-testid="deposit-page">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-border">
        {/* LEFT — method selector + details */}
        <div className="bg-card p-6 lg:p-8">
          <p className="label-eyebrow mb-2">Step 1</p>
          <h3 className="text-lg font-semibold mb-4">Choose payment method</h3>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-background border border-subtle grid grid-cols-3 w-full">
              <TabsTrigger value="btc" data-testid="dep-tab-btc" className="gap-1.5">
                <Bitcoin className="w-3.5 h-3.5" /> BTC
              </TabsTrigger>
              <TabsTrigger value="usdt" data-testid="dep-tab-usdt" className="gap-1.5">
                <Coins className="w-3.5 h-3.5" /> USDT
              </TabsTrigger>
              <TabsTrigger value="bank" data-testid="dep-tab-bank" className="gap-1.5">
                <Landmark className="w-3.5 h-3.5" /> Bank
              </TabsTrigger>
            </TabsList>

            <TabsContent value="btc" className="mt-5 space-y-4" data-testid="dep-panel-btc">
              <MethodCard
                title="Bitcoin (BTC)"
                subtitle="Send BTC only on the Bitcoin network"
                address={btcAddr}
                bg="bg-orange-500/5"
              >
                <CopyRow label="BTC Wallet Address" value={btcAddr} testId="btc-address" />
              </MethodCard>
            </TabsContent>

            <TabsContent value="usdt" className="mt-5 space-y-4" data-testid="dep-panel-usdt">
              <MethodCard
                title={`USDT (${usdtNet})`}
                subtitle={`Send USDT on the ${usdtNet} network only`}
                address={usdtAddr}
                bg="bg-emerald-500/5"
              >
                <CopyRow label={`USDT Wallet Address (${usdtNet})`} value={usdtAddr} testId="usdt-address" />
                <div className="pt-1">
                  <span className="label-eyebrow">Network</span>
                  <div className="font-mono-num text-sm mt-1">{usdtNet}</div>
                </div>
              </MethodCard>
            </TabsContent>

            <TabsContent value="bank" className="mt-5 space-y-4" data-testid="dep-panel-bank">
              <div className="border border-subtle rounded-md p-5 bg-blue-500/5">
                <div className="flex items-center gap-2 mb-4">
                  <Landmark className="w-4 h-4 text-primary" />
                  <div>
                    <h4 className="font-semibold text-sm">Bank Transfer</h4>
                    <p className="text-xs text-muted-foreground">Wire funds to the account below</p>
                  </div>
                </div>
                <CopyRow label="Bank Name" value={bank.bank_name} testId="bank-name" />
                <CopyRow label="Account Holder" value={bank.account_name} testId="bank-account-name" />
                <CopyRow label="Account Number" value={bank.account_number} testId="bank-account-number" />
                <CopyRow label="IFSC / Routing" value={bank.ifsc} testId="bank-ifsc" />
                {bank.swift && <CopyRow label="SWIFT / BIC" value={bank.swift} testId="bank-swift" />}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* RIGHT — deposit request form */}
        <form onSubmit={submit} className="bg-card p-6 lg:p-8 space-y-5">
          <div>
            <p className="label-eyebrow mb-2">Step 2</p>
            <h3 className="text-lg font-semibold">Confirm your deposit</h3>
            <p className="text-xs text-muted-foreground mt-1">Once you have transferred the funds, log the deposit here for admin approval.</p>
          </div>
          <div>
            <Label className="label-eyebrow">Amount (USD)</Label>
            <Input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-2 h-11 bg-background border-subtle font-mono-num" data-testid="deposit-amount" placeholder="0.00" required />
          </div>
          <div>
            <Label className="label-eyebrow">Transaction Reference / TXID</Label>
            <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-2 h-11 bg-background border-subtle font-mono-num" placeholder="e.g. blockchain txid or UTR" data-testid="deposit-reference" />
          </div>
          <div>
            <Label className="label-eyebrow">Note</Label>
            <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-2 bg-background border-subtle" rows={3} placeholder="Optional message to admin" data-testid="deposit-note" />
          </div>
          <Button type="submit" disabled={busy} className="w-full h-11" data-testid="submit-deposit">
            {busy ? "Submitting…" : `Submit ${tab === "btc" ? "BTC" : tab === "usdt" ? "USDT" : "Bank"} deposit request`}
          </Button>
        </form>
      </div>

      {/* History */}
      <div className="bg-card p-6 lg:p-8 border border-subtle">
        <p className="label-eyebrow mb-2">History</p>
        <h3 className="text-lg font-semibold mb-4">Your Deposits</h3>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-surface-hover border-subtle">
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Requested</TableHead>
                <TableHead>Processed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">No deposits yet</TableCell></TableRow>}
              {history.map((t) => (
                <TableRow key={t.id} className="border-subtle" data-testid={`my-dep-${t.id}`}>
                  <TableCell className="text-right font-mono-num font-semibold text-profit">+{fmtUSD(t.amount)}</TableCell>
                  <TableCell>{t.method}</TableCell>
                  <TableCell className="text-xs font-mono-num text-muted-foreground max-w-xs truncate">{t.reference || "—"}</TableCell>
                  <TableCell>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                      t.status === "pending" ? "bg-warning/15 text-warning" :
                      t.status === "approved" ? "bg-profit/15 text-profit" :
                      "bg-loss/15 text-loss"
                    }`}>{t.status.toUpperCase()}</span>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtDate(t.created_at)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtDate(t.processed_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

function MethodCard({ title, subtitle, address, bg, children }) {
  return (
    <div className={`border border-subtle rounded-md p-5 ${bg}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h4 className="font-semibold text-sm">{title}</h4>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
      </div>
      {address ? (
        <div className="grid grid-cols-[auto_1fr] gap-5 items-start">
          <div className="bg-white p-2 rounded" title="Scan to deposit">
            <QRCodeCanvas value={address} size={128} bgColor="#ffffff" fgColor="#000000" level="M" />
          </div>
          <div>{children}</div>
        </div>
      ) : (
        <div>
          <p className="text-sm text-muted-foreground">This deposit method has not been configured for your account yet. Please contact your account manager.</p>
        </div>
      )}
    </div>
  );
}
